import type {
  ApiEnvelope,
  ApiErrorShape,
  Attachment,
  CursorMeta,
  Ticket,
  TicketListResult,
} from "./types";
import {
  activeCsrfToken,
  adaptApiPath,
  adaptFormBody,
  adaptJsonBody,
  isAnonymousSession,
  normalizeApiData,
  rememberSecurityTokens,
} from "./backendContract";

import { browserWorkspace, requestWorkspace } from "./workspace";

const API_BASE = "/api/v1";

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;
  readonly requestId?: string;

  constructor(
    message: string,
    options: {
      status: number;
      code?: string;
      details?: unknown;
      requestId?: string;
    },
  ) {
    super(message);
    this.name = "ApiError";
    this.status = options.status;
    this.code = options.code ?? "request_failed";
    this.details = options.details;
    this.requestId = options.requestId;
  }
}

type RequestOptions = Omit<RequestInit, "body"> & {
  body?: unknown;
  rawBody?: BodyInit;
  idempotencyKey?: string;
};

function emitUnauthorized() {
  window.dispatchEvent(new CustomEvent("support:unauthorized"));
}


const csrfRefreshInFlight = new Map<string, Promise<string | undefined>>();

function invalidCsrfResponse(response: Response, payload: unknown) {
  if (response.status !== 403 || !payload || typeof payload !== "object") return false;
  const error = (payload as ApiErrorShape).error;
  return error?.code === "CSRF_INVALID"
    || (error?.code === "FORBIDDEN" && typeof error.message === "string" && ["A CSRF token is required", "The CSRF token is invalid"].includes(error.message));
}

async function refreshCsrfToken(path: string, requestHeaders: Headers): Promise<string | undefined> {
  if (path.startsWith("http")) return undefined;
  const scope = requestWorkspace(path, requestHeaders);
  const staff = scope === "staff";
  const existing = csrfRefreshInFlight.get(scope);
  if (existing) return existing;
  const operation = (async () => {
    const response = await fetch(`${API_BASE}${staff ? "/auth/staff/session" : "/auth/customer/session"}`, {
      method: "GET",
      credentials: "include",
      headers: { Accept: "application/json" },
      cache: "no-store",
    });
    if (response.status === 401) emitUnauthorized();
    if (!response.ok) return undefined;
    const payload = await response.json().catch(() => null) as ApiEnvelope<unknown> | null;
    const data = payload && typeof payload === "object" && "data" in payload ? payload.data : payload;
    if (!data || typeof data !== "object" || typeof (data as { csrfToken?: unknown }).csrfToken !== "string") return undefined;
    rememberSecurityTokens(data, scope);
    return (data as { csrfToken: string }).csrfToken;
  })().finally(() => { csrfRefreshInFlight.delete(scope); });
  csrfRefreshInFlight.set(scope, operation);
  return operation;
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}, allowCsrfRetry = true): Promise<T> {
  const originalPath = path;
  const requestPath = path.startsWith("http") ? path : adaptApiPath(path);
  const headers = new Headers(options.headers);
  headers.set("Accept", "application/json");

  const workspace = requestWorkspace(originalPath, headers);
  if (!path.startsWith("http")) headers.set("X-Support-Workspace", workspace);
  const token = path.startsWith("http") ? undefined : activeCsrfToken(workspace);
  if (token && !headers.has("X-CSRF-Token")) headers.set("X-CSRF-Token", token);
  if (options.idempotencyKey) headers.set("Idempotency-Key", options.idempotencyKey);

  let body = adaptFormBody(originalPath, options.rawBody);
  if (options.body !== undefined) {
    headers.set("Content-Type", "application/json");
    body = JSON.stringify(adaptJsonBody(originalPath, options.method ?? "GET", options.body, headers));
  }

  const response = await fetch(requestPath.startsWith("http") ? requestPath : `${API_BASE}${requestPath}`, {
    ...options,
    body,
    headers,
    credentials: "include",
  });

  const requestId = response.headers.get("x-request-id") ?? undefined;
  const contentType = response.headers.get("content-type") ?? "";
  const payload = contentType.includes("json")
    ? ((await response.json()) as ApiEnvelope<T> | ApiErrorShape | T)
    : undefined;

  if (!response.ok) {
    if (allowCsrfRetry && invalidCsrfResponse(response, payload)) {
      const refreshedToken = await refreshCsrfToken(originalPath, headers);
      if (refreshedToken) {
        const retryHeaders = new Headers(headers);
        retryHeaders.set("X-CSRF-Token", refreshedToken);
        return apiRequest<T>(path, { ...options, headers: retryHeaders }, false);
      }
    }
    if (response.status === 401) emitUnauthorized();
    const error = payload as ApiErrorShape | undefined;
    throw new ApiError(
      error?.error?.message ?? error?.message ?? "We couldn't complete that request.",
      {
        status: response.status,
        code: error?.error?.code,
        details: error?.error?.details,
        requestId,
      },
    );
  }

  if (response.status === 204) return undefined as T;
  let data: unknown = payload;
  if (payload && typeof payload === "object" && "data" in payload) {
    data = (payload as ApiEnvelope<T>).data;
  }
  if (!path.startsWith("http")) rememberSecurityTokens(data, workspace);
  if (isAnonymousSession(originalPath, data)) {
    throw new ApiError("No active session", { status: 401, code: "session_missing", requestId });
  }
  return normalizeApiData(originalPath, data) as T;
}

export async function apiEnvelope<T>(path: string, options: RequestOptions = {}, allowCsrfRetry = true) {
  const originalPath = path;
  const requestPath = adaptApiPath(path);
  const headers = new Headers(options.headers);
  headers.set("Accept", "application/json");
  const workspace = requestWorkspace(originalPath, headers);
  headers.set("X-Support-Workspace", workspace);
  if (options.body !== undefined) headers.set("Content-Type", "application/json");
  if (options.idempotencyKey) headers.set("Idempotency-Key", options.idempotencyKey);
  const token = activeCsrfToken(workspace);
  if (token && !headers.has("X-CSRF-Token")) headers.set("X-CSRF-Token", token);

  const response = await fetch(`${API_BASE}${requestPath}`, {
    ...options,
    headers,
    credentials: "include",
    body: adaptFormBody(originalPath, options.rawBody)
      ?? (options.body === undefined ? undefined : JSON.stringify(adaptJsonBody(originalPath, options.method ?? "GET", options.body, headers))),
  });
  const payload = (await response.json().catch(() => ({}))) as ApiEnvelope<T> & ApiErrorShape;
  if (!response.ok) {
    if (response.status === 401) emitUnauthorized();
    if (allowCsrfRetry && invalidCsrfResponse(response, payload)) {
      const refreshedToken = await refreshCsrfToken(originalPath, headers);
      if (refreshedToken) {
        const retryHeaders = new Headers(headers);
        retryHeaders.set("X-CSRF-Token", refreshedToken);
        return apiEnvelope<T>(path, { ...options, headers: retryHeaders }, false);
      }
    }
    throw new ApiError(payload.error?.message ?? "We couldn't complete that request.", {
      status: response.status,
      code: payload.error?.code,
      details: payload.error?.details,
      requestId: response.headers.get("x-request-id") ?? undefined,
    });
  }
  rememberSecurityTokens(payload.data, workspace);
  if (isAnonymousSession(originalPath, payload.data)) {
    throw new ApiError("No active session", { status: 401, code: "session_missing", requestId: response.headers.get("x-request-id") ?? undefined });
  }
  return { data: normalizeApiData(originalPath, payload.data) as T, meta: payload.meta ?? {} };
}

export function createIdempotencyKey(scope: string) {
  return `${scope}:${crypto.randomUUID()}`;
}

export function attachmentIdempotencyKey(submissionKey: string, index: number) {
  if (!submissionKey) throw new Error("An idempotency key is required to upload attachments.");
  return `${submissionKey}:attachment:${index}`;
}

export function asTicketList(data: unknown, meta: CursorMeta = {}): TicketListResult {
  if (Array.isArray(data)) return { tickets: data as Ticket[], meta };
  if (data && typeof data === "object" && "tickets" in data) {
    const value = data as { tickets?: Ticket[]; meta?: CursorMeta };
    return { tickets: value.tickets ?? [], meta: value.meta ?? meta };
  }
  return { tickets: [], meta };
}

export async function uploadAttachments(files: File[], submissionKey: string): Promise<Attachment[]> {
  const uploaded: Attachment[] = [];
  // Capture once for the whole attempt, including retries after focus/navigation.
  const workspace = browserWorkspace();
  for (const [index, file] of files.entries()) {
    const form = new FormData();
    form.append("files", file, file.name);
    const result = await apiRequest<Attachment | { attachment: Attachment }>("/attachments", {
      method: "POST",
      rawBody: form,
      headers: { "X-Support-Workspace": workspace },
      idempotencyKey: attachmentIdempotencyKey(submissionKey, index),
    });
    uploaded.push("attachment" in result ? result.attachment : result);
  }
  return uploaded;
}

export function queryString(values: Record<string, string | number | boolean | undefined | null>) {
  const params = new URLSearchParams();
  Object.entries(values).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") params.set(key, String(value));
  });
  const value = params.toString();
  return value ? `?${value}` : "";
}
